import React, { useEffect, useMemo, useReducer, useState } from "react";
import { chain, filter, keys, pluck, sortBy, uniq } from "underscore";
import WidgetModuleLayout from "./WidgetModuleLayout";
import {
    CommonWidgetMetrics,
    CrossChannelTableDataModules,
    CrossChannelTableDataModuleSections,
    GroupingKey,
    IMyProductTrendsOriginalData,
    IPerformanceComparisionRawDataItem,
    IPerformanceComparisonData,
    IPricingCompetitiveAnalysisOriginalData,
    IPricingCompetitiveAnalysisTrendsOriginalData,
    IProductAnalysisVisibilityModule,
    ISovDataItem,
    MyProductTrendsModules,
    MyProductTrendsTypes,
    PricingCharts,
    ProductDeepdiveModules,
    ProgramType,
    TCrossChannelTableData,
} from "@ts/common-types";
import {
    SummaryWidget,
    SatisfactionDriversSummaryWidget,
    KeywordVisibilityWidget,
    TrendsWithCompareModalWidget,
    PricingAndDealCompetitionAnalysisWidget,
} from "@components/widgets";
import { useCrossChannelTableData } from "@/hooks/AllCustomHooks";
import {
    visibilityFilterOptions,
    VISIBILITY_CHART_FILTERS_INTIAL_STATE,
    VISIBILITY_KEYWORD_PERFORMANCE_CHART_FILTERS_INTIAL_STATE,
    visibilityModuleRadioOptions,
    visibilityModuleMyProductTrendsRadioOptions,
} from "@/ts/constants";
import {
    chartFiltersReducer,
    formatCtrTrendData,
    formatMyProductTrends,
    formatmySovVsCompetitionData,
} from "@/ts/widget";
import { includes } from "underscore";
import MyVsCompetitorSkuSOVWidget from "../widgets/MyVsCompetitorSkuSOVWidget";
import { Checkbox, Col, Dropdown, Menu, Row, Tag } from "antd";
import CtrTrendOverTime from "../widgets/CtrTrendOverTime";
import PerformanceComparisonWidget from "../widgets/PerformanceComparisonWidget";
import { LuListFilter } from "react-icons/lu";
import { CHANNEL_CONFIGS } from "@/config/channel-configs";

const ProductAnalysisVisibilityModule: React.FC<
    IProductAnalysisVisibilityModule
> = ({
    loadingState,
    selectedSKU,
    visibilityData,
    channel,
    currencySymbol,
}) => {
    const [showPricingAndDealsTrends, setShowPricingAndDealsTrends] =
        useState(false);
    const [showKeywordPerformance, setShowKeywordPerformance] = useState(false);
    const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const [chartFilters, setChartFilters] = useReducer(
        chartFiltersReducer,
        VISIBILITY_CHART_FILTERS_INTIAL_STATE
    );
    const [chartFiltersKeywordPerformance, setChartFiltersKeywordPerformance] =
        useReducer(
            chartFiltersReducer,
            VISIBILITY_KEYWORD_PERFORMANCE_CHART_FILTERS_INTIAL_STATE
        );

    const excludeSelectedSku = (
        dataToFilter:
            | IPricingCompetitiveAnalysisOriginalData
            | IPricingCompetitiveAnalysisTrendsOriginalData[]
    ) => filter(dataToFilter, (data) => data.sku !== selectedSKU);

    const crossChannelTableData = useCrossChannelTableData(
        visibilityData?.myProductTrendsCrossChannelTableData ?? [],
        excludeSelectedSku(
            visibilityData?.myProductTrendsCrossChannelExpandedTableData ?? []
        ),
        CrossChannelTableDataModules.VISIBILITY
    );

    const skuFilter = (
        dataToFilter:
            | IPricingCompetitiveAnalysisOriginalData
            | IPricingCompetitiveAnalysisTrendsOriginalData[],
        competitonSku: string[]
    ) =>
        filter(
            dataToFilter,
            (data) =>
                data.sku === selectedSKU || includes(competitonSku, data.sku)
        );

    const competitionTableData = useCrossChannelTableData(
        visibilityData?.myProductTrendsCompetitionTableData ?? [],
        excludeSelectedSku(
            visibilityData?.myProductTrendsCompetitionExpandedTableData
        ),
        CrossChannelTableDataModules.VISIBILITY
    );

    const keywordVisibilityCrossChannelTableData = useCrossChannelTableData(
        visibilityData?.keywordPerformanceTableCrossChannel?.[selectedKeyword],
        excludeSelectedSku(null),
        CrossChannelTableDataModules.VISIBILITY,
        CrossChannelTableDataModuleSections.KEYWORD_VISIBILITY
    );

    const myProductTrendsData = useMemo(() => {
        const competitonSku =
            chartFilters[PricingCharts.PRICE_DEALS_COMPETITION];

        const skusWithTitle = visibilityData.myProductTrendCompetition
            ? chain(visibilityData.myProductTrendCompetition)
                  .filter(({ sku }) => sku !== selectedSKU)
                  .uniq(CommonWidgetMetrics.SKU)
                  .map(({ sku }) => ({ sku }))
                  .value()
            : [];

        const generateSection = (
            data: Partial<IMyProductTrendsOriginalData>[],
            tableData: TCrossChannelTableData[],
            expandedTableData: TCrossChannelTableData[],
            groupBy: string | null = null
        ) => ({
            charts: {
                [MyProductTrendsTypes.ORGANIC_VISIBILITY]:
                    formatMyProductTrends(
                        data,
                        MyProductTrendsTypes.ORGANIC_VISIBILITY,
                        groupBy
                    ),
                [MyProductTrendsTypes.PAID_VISIBILITY]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.PAID_VISIBILITY,
                    groupBy
                ),
                [MyProductTrendsTypes.OVERALL_VISIBILITY]:
                    formatMyProductTrends(
                        data,
                        MyProductTrendsTypes.OVERALL_VISIBILITY,
                        groupBy
                    ),
            },
            table: { tableData, expandedTableData },
        });

        return {
            [MyProductTrendsModules.CROSS_CHANNEL]: {
                channels: uniq(
                    pluck(
                        visibilityData.myProductTrendCrossChannel,
                        CommonWidgetMetrics.CHANNEL_ID
                    )
                ),
                ...generateSection(
                    visibilityData.myProductTrendCrossChannel,
                    crossChannelTableData.tableData,
                    crossChannelTableData.expandedTableData,
                    GroupingKey.CHANNEL
                ),
            },
            [MyProductTrendsModules.COMPETITION]: {
                skus: skusWithTitle,
                ...generateSection(
                    skuFilter(
                        visibilityData.myProductTrendCompetition,
                        competitonSku
                    ),
                    competitionTableData.tableData,
                    competitionTableData.expandedTableData,
                    CommonWidgetMetrics.SKU
                ),
            },
        };
    }, [
        visibilityData,
        chartFilters[PricingCharts.PRICE_DEALS],
        chartFilters[PricingCharts.PRICE_DEALS_COMPETITION],
        crossChannelTableData,
        competitionTableData,
    ]);

    const keywordPerformaceData = useMemo(() => {
        const competitonSku =
            chartFiltersKeywordPerformance[
                PricingCharts.PRICE_DEALS_COMPETITION
            ];

        const skusWithTitle = visibilityData
            ?.keywordPerformanceGraphCompetition?.[selectedKeyword]
            ? chain(
                  visibilityData.keywordPerformanceGraphCompetition[
                      selectedKeyword
                  ]
              )
                  .filter(({ sku }) => sku !== selectedSKU)
                  .uniq(CommonWidgetMetrics.SKU)
                  .map(({ sku, sku_title }) => ({ sku, sku_title }))
                  .value()
            : [];

        const generateSection = (
            data: Partial<IMyProductTrendsOriginalData>[],
            tableData: TCrossChannelTableData[],
            expandedTableData: TCrossChannelTableData[],
            groupBy: string | null = null
        ) => ({
            charts: {
                [MyProductTrendsTypes.OVERALL_RANK]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.OVERALL_RANK,
                    groupBy
                ),
                [MyProductTrendsTypes.ORGANIC_RANK]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.ORGANIC_RANK,
                    groupBy
                ),
                [MyProductTrendsTypes.PAID_RANK]: formatMyProductTrends(
                    data,
                    MyProductTrendsTypes.PAID_RANK,
                    groupBy
                ),
            },
            table: { tableData, expandedTableData },
        });

        return {
            [MyProductTrendsModules.CROSS_CHANNEL]: {
                channels: uniq(
                    pluck(
                        visibilityData?.keywordPerformanceGraphCrossChannel?.[
                            selectedKeyword
                        ],
                        "channel"
                    )
                ),
                ...generateSection(
                    visibilityData?.keywordPerformanceGraphCrossChannel?.[
                        selectedKeyword
                    ] ?? [],
                    keywordVisibilityCrossChannelTableData.tableData,
                    keywordVisibilityCrossChannelTableData.expandedTableData,
                    GroupingKey.CHANNEL
                ),
            },
            [MyProductTrendsModules.COMPETITION]: {
                skus: skusWithTitle,
                ...generateSection(
                    skuFilter(
                        visibilityData?.keywordPerformanceGraphCompetition?.[
                            selectedKeyword
                        ] ?? [],
                        competitonSku
                    ),
                    keywordVisibilityCrossChannelTableData.tableData,
                    keywordVisibilityCrossChannelTableData.expandedTableData,
                    CommonWidgetMetrics.SKU
                ),
            },
        };
    }, [
        visibilityData,
        chartFilters[PricingCharts.PRICE_DEALS],
        chartFilters[PricingCharts.PRICE_DEALS_COMPETITION],
        crossChannelTableData,
        competitionTableData,
        selectedKeyword,
    ]);

    useEffect(() => {
        //! Note: The below code is to set the top 5 skus for the price-deals-skus filter

        const skusWithPrices = visibilityData?.myProductTrendCompetition?.map(
            (item: IPricingCompetitiveAnalysisOriginalData) => ({
                sku: item.sku,
                price: item.price ?? 0,
            })
        );

        const sortedSkus = sortBy(
            uniq(skusWithPrices, CommonWidgetMetrics.SKU),
            MyProductTrendsTypes.PRICE
        ).reverse();

        const topSkus = filter(sortedSkus, (item) => item.sku !== selectedSKU)
            .slice(0, 5)
            .map((item) => item.sku);

        setChartFilters({
            type: PricingCharts.PRICE_DEALS_SKUS,
            payload: topSkus,
        });

        setChartFilters({
            type: PricingCharts.PRICE_DEALS_COMPETITION,
            payload: topSkus,
        });
    }, [visibilityData.myProductTrendCompetition]);

    const renderWidget = (
        show: boolean,
        setShow: React.Dispatch<React.SetStateAction<boolean>>,
        title: string,
        widgetData: any,
        additionalProps: { [key: string]: any } = {}
    ) => {
        if (!show) return null;

        return (
            <PricingAndDealCompetitionAnalysisWidget
                title={title}
                widgetData={widgetData}
                show={show}
                setShow={setShow}
                {...(additionalProps as any)}
            />
        );
    };
    const handleFilterChange = (checkedValues: string[]) => {
        setSelectedFilters(checkedValues);
    };

    const handleRemoveFilter = (filter: string) => {
        setSelectedFilters(selectedFilters.filter((f: string) => f !== filter));
    };

    const filterPerformanceData = (
        data:
            | IPerformanceComparisionRawDataItem[]
            | IPerformanceComparisonData[],
        selectedFilters: string[]
    ) => {
        if (
            selectedFilters.length === 0 ||
            ![ProgramType.SP, ProgramType.SB, ProgramType.SD].some(
                (type) =>
                    type.toLowerCase() === selectedFilters[0].toLowerCase()
            )
        ) {
            return data;
        }

        if (!Array.isArray(data)) return [];

        return (
            data as (
                | IPerformanceComparisionRawDataItem
                | IPerformanceComparisonData
            )[]
        ).filter((item) => {
            const programValue =
                "programType" in item ? item.programType : item.program_type;
            return programValue && selectedFilters.includes(programValue);
        });
    };

    const filterMyProductSovData = (
        data: ISovDataItem[],
        selectedFilters: string[]
    ) => {
        if (selectedFilters.length === 0) return data;

        let filteredData = data;

        if (selectedFilters.includes("page_1")) {
            filteredData = filteredData.filter(
                (item: any) => item.page_number === 1
            );
        }
        if (selectedFilters.includes("top_5")) {
            filteredData = filteredData.filter(
                (item: any) => item.page_number <= 5
            );
        }
        if (selectedFilters.includes("top_10")) {
            filteredData = filteredData.filter(
                (item: any) => item.page_number <= 10
            );
        }

        return filteredData;
    };

    const filtersMenu = (
        <Menu style={{ height: "200px", overflowY: "auto" }}>
            {Object.entries(visibilityFilterOptions).map(
                ([title, options], index) => (
                    <React.Fragment key={title}>
                        <Menu.ItemGroup title={title}>
                            <Checkbox.Group
                                onChange={(checkedValues) =>
                                    handleFilterChange(
                                        checkedValues as string[]
                                    )
                                }
                                value={selectedFilters}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {options.map(({ label, value }) => (
                                    <Checkbox key={value} value={value}>
                                        {label}
                                    </Checkbox>
                                ))}
                            </Checkbox.Group>
                        </Menu.ItemGroup>
                        {index !==
                            Object.entries(visibilityFilterOptions).length -
                                1 && <Menu.Divider />}
                    </React.Fragment>
                )
            )}
            <Menu.Divider />
        </Menu>
    );

    const filterComponent = () => {
        return (
            <Dropdown overlay={filtersMenu} trigger={["click"]}>
                <span
                    className="me-3 p-1 border rounded"
                    style={{ cursor: "pointer" }}
                >
                    <LuListFilter style={{ fontSize: "22px" }} />
                </span>
            </Dropdown>
        );
    };

    const filterPillsComponent = () => {
        const allFilterOptions = Object.values(visibilityFilterOptions).flat();

        return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {selectedFilters.map((filter) => {
                    const option = allFilterOptions.find(
                        (opt) => opt.value === filter
                    );
                    return (
                        <Tag
                            key={filter}
                            closable
                            onClose={() => handleRemoveFilter(filter)}
                            style={{
                                backgroundColor: "#1677ff",
                                color: "white",
                                border: "none",
                                fontSize: "14px",
                                padding: "4px 10px",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {option ? option.label : filter}
                        </Tag>
                    );
                })}
            </div>
        );
    };

    return (
        <WidgetModuleLayout
            loaderCondition={
                keys(visibilityData).length === 0 ||
                loadingState?.[ProductDeepdiveModules.VISIBILITY]
            }
        >
            <SummaryWidget
                widgetData={visibilityData.organicPaidGV}
                activeTab={ProductDeepdiveModules.VISIBILITY}
            />
            <SatisfactionDriversSummaryWidget
                moduleType={ProductDeepdiveModules.VISIBILITY}
                widgetData={visibilityData.workingNotWorking}
            />
            <TrendsWithCompareModalWidget
                title="Visibility Trends Over Time"
                widgetLinkName="Compare"
                widgetData={visibilityData.visibilityTrends}
                graphAttributes={{
                    yAxisTitle: "Paid Visibility",
                    yAxisOppositeTitle: "Organic Visibility",
                    yAxisOppositeTitleOptional: "Overall Visibility",
                    yAxisOppositeDashStyleReq: true,
                }}
                onWidgetLinkHandler={() => setShowPricingAndDealsTrends(true)}
                additionalTitleComponent={filterComponent}
                filterPillsComponent={filterPillsComponent}
            />

            <Row className="my-4" justify="center" align="middle" gutter={16}>
                <Col span={12}>
                    <PerformanceComparisonWidget
                        widgetData={
                            filterPerformanceData(
                                (visibilityData?.performanceComparisionData as IPerformanceComparisonData[]) ??
                                    [],
                                selectedFilters
                            ) as IPerformanceComparisonData[]
                        }
                        additionalTitleComponent={filterComponent}
                        filterPillsComponent={filterPillsComponent}
                    />
                </Col>
                <Col span={12}>
                    <CtrTrendOverTime
                        widgetData={formatCtrTrendData(
                            filterPerformanceData(
                                visibilityData?.ctrTrendData ?? [],
                                selectedFilters
                            ) as IPerformanceComparisionRawDataItem[]
                        )}
                        additionalTitleComponent={filterComponent}
                        filterPillsComponent={filterPillsComponent}
                    />
                </Col>
            </Row>

            <MyVsCompetitorSkuSOVWidget
                widgetData={formatmySovVsCompetitionData(
                    filterMyProductSovData(
                        visibilityData?.mySovVsCompetitionData ?? [],
                        selectedFilters
                    )
                )}
                selectedSku={selectedSKU}
                additionalTitleComponent={filterComponent}
                filterPillsComponent={filterPillsComponent}
            />
            <KeywordVisibilityWidget
                title="Keyword Visibility"
                widgetData={visibilityData?.keywordVisibility ?? []}
                setShow={setShowKeywordPerformance}
                setSelectedKeyword={setSelectedKeyword}
            />
            {renderWidget(
                showPricingAndDealsTrends,
                setShowPricingAndDealsTrends,
                `My Products Trends on ${CHANNEL_CONFIGS[channel].name}`,
                myProductTrendsData,
                {
                    chartFilters,
                    setChartFilters,
                    currencySymbol,
                    radioOptions: visibilityModuleMyProductTrendsRadioOptions,
                }
            )}

            {renderWidget(
                showKeywordPerformance,
                setShowKeywordPerformance,
                "Keyword Performance",
                keywordPerformaceData,
                {
                    chartFilters: chartFiltersKeywordPerformance,
                    setChartFilters: setChartFiltersKeywordPerformance,
                    currencySymbol,
                    radioOptions: visibilityModuleRadioOptions,
                }
            )}
        </WidgetModuleLayout>
    );
};

export default ProductAnalysisVisibilityModule;
